import React from "react";
import PropTypes from "prop-types";
import { SFStack } from "../../grid";
import SFCheckbox from "../../checkbox";
import SFTypography from "../../typography";

/**
 * PropTypes definition for the CheckboxFilter component.
 * This is exported for reuse across multiple components that use checkbox filters.
 */
export const CheckboxFilterPropTypes = {
  /**
   * The heading displayed above the group of checkboxes.
   */
  label: PropTypes.string.isRequired,

  /**
   * Handler for checkbox changes. When used directly on the CheckboxFilter component,
   * it receives three arguments:
   * @param {string} checkboxValue - The value of the changed checkbox (or label if no value provided)
   * @param {boolean} checked - The new checked state
   * @param {Array} updatedCheckboxes - Complete array of all checkboxes with their updated states
   *
   * Note: In parent components using the unified handler pattern, this prop is typically not needed
   * as changes are handled via the parent component's unified onChange handler.
   */
  onChange: PropTypes.func,

  /**
   * Array of checkbox configuration objects.
   */
  checkboxes: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Optional unique identifier for the checkbox. If not provided, array index is used as key.
       */
      id: PropTypes.string,

      /**
       * The label text displayed next to the checkbox.
       */
      label: PropTypes.string.isRequired,

      /**
       * The value of the checkbox. If not provided, label is used as value.
       */
      value: PropTypes.string,

      /**
       * Whether the checkbox is checked.
       */
      checked: PropTypes.bool,

      /**
       * Whether the checkbox is disabled.
       */
      disabled: PropTypes.bool,

      /**
       * Size of the checkbox component.
       */
      size: PropTypes.oneOf(["small", "medium", "large"]),

      /**
       * Additional props to pass to the SFCheckbox component.
       */
      props: PropTypes.object,
    })
  ).isRequired,
};

const CheckboxFilter = ({ label, checkboxes, onChange }) => {
  // Check if we have checkboxes
  const hasCheckboxes =
    checkboxes && Array.isArray(checkboxes) && checkboxes.length > 0;
  /**
   * Handles checkbox state changes and calls the onChange handler with updated data
   *
   * @param {string} checkboxValue - The value of the checkbox that changed
   * @param {boolean} checked - The new checked state
   */
  const handleCheckboxChange = (checkboxValue, checked) => {
    if (onChange) {
      // Create a copy of the checkboxes array with the updated state
      const updatedCheckboxes = checkboxes.map((checkbox) => {
        const value = checkbox.value || checkbox.label;
        if (value === checkboxValue) {
          return { ...checkbox, checked };
        }
        return checkbox;
      });

      // Call onChange with the updated selection state
      // This provides a complete view of all checkboxes after the change
      onChange(checkboxValue, checked, updatedCheckboxes);
    }
  };

  if (!hasCheckboxes) return null;

  return (
    <SFStack width="100%">
      <SFTypography variant="formElementLabel">{label}</SFTypography>
      <SFStack direction="row" alignItems="center" width="100%" flexWrap="wrap">
        {checkboxes.map((checkbox, checkboxIndex) => (
          <SFCheckbox
            key={checkbox.id || checkboxIndex}
            label={checkbox.label}
            checked={checkbox.checked || false}
            disabled={checkbox.disabled || false}
            onChange={(e) =>
              handleCheckboxChange(
                checkbox.value || checkbox.label,
                e.target.checked
              )
            }
            size={checkbox.size || "medium"}
            {...(checkbox.props || {})} // Spread additional checkbox props
          />
        ))}
      </SFStack>
    </SFStack>
  );
};

// Use the exported prop types
CheckboxFilter.propTypes = CheckboxFilterPropTypes;

export default CheckboxFilter;
